<template>
  <div ref="root" class="flex flex-col gap-2">
    <p v-if="detail.linesSyncing" class="m-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">PO lines are syncing, try again in a minute.</p>

    <template v-if="detail.openRequest">
      <RequestLine
        :ship-by="detail.openRequest.shipBy"
        :to-plan="detail.summary.toPlan"
        :note="detail.openRequest.note"
        :requested-at="detail.openRequest.requestedAt"
        :requested-by="detail.openRequest.requestedBy"
        :can-withdraw="planner"
        :can-answer="answerer && !detail.linesSyncing"
        :answering="answering"
        :withdrawing="withdrawing"
        @withdraw="withdrawOpen = true"
        @answer="store.startAnswer(detail.poNumber)"
      />
      <AnswerRequestForm v-if="answering" :po-number="detail.poNumber" :request="detail.openRequest" :lines="detail.lines" :shipments="detail.shipments" @close="closeAnswer" />
    </template>

    <!-- A table to assistive tech only in the grid layout; the compact rows wrap freely. -->
    <div class="flex flex-col gap-2" :role="tableRole('table')" :aria-label="compact ? undefined : `Shipments of ${detail.poNumber}`">
      <div v-if="!compact" :class="SHIPMENT_GRID" class="grid gap-3 border-b border-gray-200 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500" role="row">
        <span role="columnheader">Ship date</span>
        <span class="text-right" role="columnheader">Pcs · lines</span>
        <span role="columnheader">State</span>
        <span role="columnheader">Details</span>
        <span role="columnheader">Note</span>
        <span role="columnheader"><span class="sr-only">Actions</span></span>
      </div>
      <div v-if="shipments.length === 0 && !planning" :role="tableRole('row')">
        <p class="m-0 py-2 text-sm text-gray-500" :role="tableRole('cell')" :aria-colspan="compact ? undefined : SHIPMENT_COLUMN_COUNT">No shipments planned yet.</p>
      </div>
      <template v-for="s in shipments" :key="s.id">
        <ShipmentRow
          :shipment="s"
          :lines="detail.lines"
          :event="s.shippingEventId ? (events.get(s.shippingEventId) ?? null) : null"
          :today="today"
          :partner="partner"
          :can-edit="!detail.linesSyncing"
          :compact="compact"
          @edit="openForm($event)"
          @mark-shipped="emit('mark-shipped', $event)"
          @shipping-details="emit('shipping-details', $event)"
        />
        <div v-if="form && form.editing?.id === s.id" :key="`edit-${s.id}`" :role="tableRole('row')">
          <div :role="tableRole('cell')" :aria-colspan="compact ? undefined : SHIPMENT_COLUMN_COUNT">
            <PlanShipmentForm :po-number="detail.poNumber" :vendor-id="vendorId" :lines="detail.lines" :editing="s" @close="closeForm" />
          </div>
        </div>
      </template>
    </div>
    <PlanShipmentForm v-if="planning" key="new" :po-number="detail.poNumber" :vendor-id="vendorId" :lines="detail.lines" :editing="null" @close="closeForm" />

    <div class="flex flex-wrap items-center gap-4 border-t border-gray-200 pt-3 text-sm">
      <button v-if="planner" type="button" class="font-semibold text-blue-700 hover:underline" @click="copyLink">Copy link</button>
      <span v-if="detail.summary.toPlan > 0" class="text-gray-600">
        {{ toPlanSummary(detail.summary.toPlan, detail.summary.ordered) }}
      </span>
      <span class="ml-auto flex items-center gap-3">
        <button v-if="planner && detail.summary.toPlan > 0 && !detail.openRequest" type="button" class="btn-secondary btn-sm" @click="emit('request-delivery', detail.poNumber)">
          Request delivery date
        </button>
        <button v-if="partner && !detail.linesSyncing && !planning && !answering" type="button" :class="DASHED_BUTTON" data-return-focus="plan" @click="openForm(null)">
          + Plan a shipment
        </button>
      </span>
    </div>

    <ConfirmDialog
      :open="withdrawOpen"
      title="Withdraw request"
      :message="`Withdraw the delivery request for ${detail.poNumber}?`"
      confirm-label="Withdraw request"
      @cancel="withdrawOpen = false"
      @confirm="withdraw"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import AnswerRequestForm from '@/components/poTracker/AnswerRequestForm.vue';
  import PlanShipmentForm from '@/components/poTracker/PlanShipmentForm.vue';
  import RequestLine from '@/components/poTracker/RequestLine.vue';
  import ShipmentRow from '@/components/poTracker/ShipmentRow.vue';
  import { SHIPMENT_COLUMN_COUNT, SHIPMENT_GRID, SHIPMENT_GRID_MIN_WIDTH } from '@/components/poTracker/poTrackerGrid';
  import { DASHED_BUTTON } from '@/components/poTracker/poTrackerStyles';
  import { useAccess } from '@/composables/useAccess';
  import { useElementWidth } from '@/composables/useElementWidth';
  import { useFocusReturn } from '@/composables/useFocusReturn';
  import logger from '@/services/logger';
  import { withdrawRequest } from '@/services/poTrackerService';
  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import { PERMISSIONS } from '@/types/access';
  import type { PoDetail, Shipment, ShippingEvent } from '@/types/poTracker';
  import { errorStatus, extractErrorMessage } from '@/utils/apiError';
  import { canAnswerRequests, isoToday, poLink, sortShipments, toPlanSummary } from '@/utils/poTracker';

  const HTTP_NOT_FOUND = 404;
  const HTTP_FORBIDDEN = 403;

  const props = defineProps<{ detail: PoDetail }>();
  const emit = defineEmits<{ 'mark-shipped': [shipment: Shipment]; 'shipping-details': [event: ShippingEvent]; 'request-delivery': [poNumber: string] }>();

  const store = usePoTrackerStore();
  const root = ref<HTMLElement | null>(null);
  const width = useElementWidth(root);
  // Below the grid's minimum the rows wrap instead, so Edit and Mark shipped stay in view.
  const compact = computed(() => width.value > 0 && width.value < SHIPMENT_GRID_MIN_WIDTH);
  const tableRole = (role: string): string | undefined => (compact.value ? undefined : role);
  const { hasPermission } = useAccess();

  const partner = computed(() => hasPermission(PERMISSIONS.WRITE_PO_SHIPMENTS));
  const planner = computed(() => hasPermission(PERMISSIONS.REQUEST_PO_DELIVERY));
  const answerer = computed(() => canAnswerRequests(hasPermission));

  // One open form per PO: a new shipment, or the edit of one shipment.
  const form = ref<{ editing: Shipment | null } | null>(null);
  const planning = computed(() => form.value !== null && form.value.editing === null);

  const withdrawOpen = ref(false);
  const withdrawing = ref(false);
  const answering = computed(() => answerer.value && store.answering === props.detail.poNumber && props.detail.openRequest !== null);

  // Closing a form puts focus back on the button that opened it (Edit, + Plan a shipment, Answer);
  // a button hidden while its form was open is found again by its data-return-focus.
  const formFocus = useFocusReturn();
  const answerFocus = useFocusReturn();
  const returnTarget = (name: string) => (): HTMLElement | null => root.value?.querySelector<HTMLElement>(`[data-return-focus="${name}"]`) ?? null;

  // One form at a time: answering closes a half-filled plan form.
  watch(answering, (open) => {
    if (!open) return;
    form.value = null;
    answerFocus.remember();
  });

  const vendorId = computed(() => store.url.vendorId ?? '');
  const today = computed(() => isoToday());
  const shipments = computed(() => sortShipments(props.detail.shipments));
  const events = computed(() => new Map(props.detail.shippingEvents.map((e) => [e.id, e])));

  // One form at a time: a plan or edit form closes the answer form of this PO.
  function openForm(editing: Shipment | null): void {
    if (store.answering === props.detail.poNumber) store.answering = null;
    formFocus.remember();
    form.value = { editing };
  }

  function closeForm(): void {
    form.value = null;
    formFocus.restore(returnTarget('plan'));
  }

  function closeAnswer(): void {
    store.answering = null;
    answerFocus.restore(returnTarget('answer'));
  }

  async function withdraw(): Promise<void> {
    const request = props.detail.openRequest;
    withdrawOpen.value = false;
    if (!request || withdrawing.value) return;
    withdrawing.value = true;
    try {
      await withdrawRequest(request.id);
      logger.showSuccess('Delivery request withdrawn');
    } catch (withdrawError) {
      // A 404 means it was already withdrawn or answered; the refresh below shows its real state.
      if (errorStatus(withdrawError) === HTTP_NOT_FOUND) logger.showInfo('Already withdrawn.');
      // The interceptor toasts a 403 even for an inline request.
      else if (errorStatus(withdrawError) !== HTTP_FORBIDDEN) logger.showError(extractErrorMessage(withdrawError));
    } finally {
      withdrawing.value = false;
      await store.afterWrite([props.detail.poNumber]);
    }
  }

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(poLink(location.origin, vendorId.value, props.detail.poNumber));
      logger.showSuccess(`Link to ${props.detail.poNumber} copied`);
    } catch {
      logger.showError("Couldn't copy the link. Copy it from the address bar.");
    }
  }
</script>
